import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { manageEmailPreferencesActions } from "../../../../../../actions/manage-email-preferences.actions";
import { routes } from '../../../../../../constants/routes';
import { isRequesting, isRequestNone, isRequestSuccess } from "../../../../../../utils/request-state.utils";
import { Preloader } from "../../../../../common/Preloader";
import { Popup, PopupBody, PopupFooter } from "../../../../../controls"
import { SavedFiltersAlerts } from "../../SavedFiltersAlerts";
import { useAppSelector } from "../../../../../../effects/useAppSelector";
import { usePageConfig } from "../../../../../common/pageConfig/usePageConfig";
import { PageConfigType } from "../../../../../../types/page-config/PageConfigType";
import { BwicFilterType } from "../../../../../../types/filters/FilterState";
import { DealerInventoryAlertsSaveButton } from "./DealerInventoryAlertsSaveButton";
import { EmailPreferencesTab } from "../../EmailPreferencesContent";
import { UserConfigFilter } from "../../../../../../types/user-config/UserConfigFilter";
import { SubscriptionFeature } from "../../../../../../types/billing/SubscriptionFeature";
import { EmailPreferencesRadioGroup } from "../EmailPreferencesRadioGroup";
import { AlertOption } from "../../../../../../types/email-preferences/EmailPreferences";
import { FilterColumnsKeys } from "../../../../../amrPipeline/aggregated/email-alerts/issuance-monitor/filterColumns";
import { textLabels } from "../../../../../../constants/text-labels";

interface DealerInventoryAlertsPopupProps {
    onClose: () => void
}

export const DealerInventoryAlertsPopup = ({ onClose }: DealerInventoryAlertsPopupProps) => {
    const dispatch = useDispatch();

    const requestStateGetPreferences = useAppSelector(s => s.manageEmailPreferences.requestStateGetPreferences);
    const dealerInventoryAlertState = useAppSelector(s => s.manageEmailPreferences.editPreferences.dealerInventoryAlertState);
    const requestStateSave = useAppSelector(s => s.manageEmailPreferences.requestStateSaveEditPreferences);
    const filter = useAppSelector(s => s.filters.inventory.filter);
    const visibleFilters = useAppSelector(s => s.filters.inventory.visibleFilters);

    const { config, requestState, updateRequestState, saveAlerts } = usePageConfig(
        PageConfigType.DealerInventory,
        false,
        BwicFilterType.Inventory
    );

    const isLoading = isRequesting(
        requestStateGetPreferences,
        requestState,
        updateRequestState
    );

    useEffect(() => {
        if (isRequestSuccess(requestStateSave)) {
            onClose();
        } // eslint-disable-next-line
    }, [requestStateSave])

    useEffect(() => () => {
        dispatch(manageEmailPreferencesActions.reset())
    }, [dispatch])

    useEffect(() => {
        if (isRequestNone(requestStateGetPreferences)) {
            dispatch(manageEmailPreferencesActions.getInitialDataRequest());
        }
    }, [dispatch, requestStateGetPreferences]);

    const handleSave = (updatedFilters?: UserConfigFilter[]) => {
        dispatch(manageEmailPreferencesActions.saveEmailPreferencesRequest());
        if (updatedFilters?.length) {
            saveAlerts(updatedFilters);
        }
    }

    return (
        <Popup
            renderInBody={true}
            title="Inventory Alerts"
            modalClass="modal-email-preferences"
            onClose={onClose}
        >
            <PopupBody className="notifications-center">
                <EmailPreferencesRadioGroup
                    value={dealerInventoryAlertState}
                    disabled={isRequesting(updateRequestState)}
                    alertOptions={[AlertOption.Never, AlertOption.Instant]}
                    alertOptionInfo={{
                        [AlertOption.Instant]: 'Alerts are sent once the security has been added, updated, or marked as traded in the Dealer Inventory.'
                    }}
                    groupNameIndentifier="dealer-inventory-alert-state"
                    title="Inventory"
                    description="Receive email notifications when security has been added, updated, or marked as traded in the Dealer Inventory."
                    onChange={alertOption => dispatch(manageEmailPreferencesActions.setDealerInventoryAlertState(alertOption))}
                    requiredFeature={SubscriptionFeature.InventoryAlerts}
                />
                <Preloader inProgress={isLoading}>
                    <div className="flex-row flex-direction-column flex-row-full-size">
                        <SavedFiltersAlerts
                            excludeColumns={[FilterColumnsKeys.Daily]}
                            filter={filter}
                            filterAlertsStatuses={config?.filters}
                            visibleFilters={visibleFilters}
                            filterType={BwicFilterType.Inventory}
                            pageConfigType={PageConfigType.DealerInventory}
                            requiredFeature={SubscriptionFeature.InventoryAlerts}
                            description={textLabels.savedFiltersInventory}
                            alertOptionToolitps={{
                                [AlertOption.Instant]: textLabels.savedFiltersInventoryInstantTooltip
                            }}
                        />
                    </div>
                </Preloader>
            </PopupBody>
            <PopupFooter>
                <Link to={routes.profileNotificationsTab(EmailPreferencesTab.DealerInventory)} className="btn-link margin-r-a">
                    Manage Alerts
                </Link>
                <button className="btn btn-ghost" onClick={onClose}>
                    Cancel
                </button>
                <DealerInventoryAlertsSaveButton
                    currentFilters={config?.filters}
                    disabled={isLoading}
                    onSave={handleSave}
                />
            </PopupFooter>
        </Popup>
    )
}
